import { ControlledSelect, ControlledTextarea } from '@/components/controlled';
import { useFormContext, useWatch } from 'react-hook-form';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui';
import { getSelectOptions, nameof } from '@/functions';
import { AdminUserPermissionsEnum } from '@/lib/enum';
import { LabelRequired } from '@/components/hoc';
import { Coins, EyeOff, Rocket } from 'lucide-react';
import { useUserConnected } from '@/hooks';
import { ProductsEnum } from '@/enums';
import { MainTabEnum } from './mainTab.enum';
import { TFormData } from '.';

const PRODUCT_OPTIONS_BY_TAB: Record<MainTabEnum, ProductsEnum[]> = {
  [MainTabEnum.COINS]: [ProductsEnum.FREE_COINS, ProductsEnum.PAID_COINS],
  [MainTabEnum.INCOGNITO]: [ProductsEnum.PAID_INCOGNITO, ProductsEnum.BONUS_INCOGNITO],
  [MainTabEnum.BOOST]: [ProductsEnum.PAID_BOOST, ProductsEnum.BONUS_BOOST],
};

const FormSetCoinsToUserBody = () => {

  const { control, setValue, unregister, formState: { isSubmitting } } = useFormContext<TFormData>();
  const { hasPermissions } = useUserConnected();
  const mainTab = useWatch({ control, name: 'mainTab' });

  const canCoins = hasPermissions(AdminUserPermissionsEnum.ADD_REMOVE_USER_COINS);
  const canIncognito = hasPermissions(AdminUserPermissionsEnum.ADD_REMOVE_USER_INCOGNITO);
  const canBoost = hasPermissions(AdminUserPermissionsEnum.ADD_REMOVE_USER_BOOST);

  const handleOnTabChange = (value: string) => {
    unregister(['coinPackageName', 'incognitoPackageName', 'boostPackageName', 'product']);
    setValue('mainTab', value as MainTabEnum);
  };

  return (
    <div className='py-3 space-y-4'>
      <Tabs value={mainTab} onValueChange={handleOnTabChange}>
        <TabsList className='grid w-full' style={{ gridTemplateColumns: `repeat(${[canCoins, canIncognito, canBoost].filter(Boolean).length}, 1fr)` }}>
          {
            canCoins &&
            <TabsTrigger value={MainTabEnum.COINS} className='flex items-center space-x-2'>
              <Coins size={16} className='text-yellow-500' />
              <span>Pièces</span>
            </TabsTrigger>
          }
          {
            canIncognito &&
            <TabsTrigger value={MainTabEnum.INCOGNITO} className='flex items-center space-x-2'>
              <EyeOff size={16} className='text-slate-500' />
              <span>Incognito</span>
            </TabsTrigger>
          }
          {
            canBoost &&
            <TabsTrigger value={MainTabEnum.BOOST} className='flex items-center space-x-2'>
              <Rocket size={16} className='text-orange-500' />
              <span>Boost</span>
            </TabsTrigger>
          }
        </TabsList>
      </Tabs>

      {
        mainTab === MainTabEnum.COINS &&
        <div className='flex flex-col gap-2'>
          <LabelRequired>Package de pièces</LabelRequired>
          <ControlledSelect
            disabled={isSubmitting}
            control={control}
            rules={{ required: 'Requis' }}
            name={nameof<TFormData>('coinPackageName')}
            placeholder='Choisir un package'
            options={getSelectOptions('CoinPackageNameEnum')} />
        </div>
      }
      {
        mainTab === MainTabEnum.INCOGNITO &&
        <div className='flex flex-col gap-2'>
          <LabelRequired>Package incognito</LabelRequired>
          <ControlledSelect
            disabled={isSubmitting}
            control={control}
            rules={{ required: 'Requis' }}
            name={nameof<TFormData>('incognitoPackageName')}
            placeholder='Choisir un package'
            options={getSelectOptions('IncognitoPackageNameEnum')} />
        </div>
      }
      {
        mainTab === MainTabEnum.BOOST &&
        <div className='flex flex-col gap-2'>
          <LabelRequired>Package boost</LabelRequired>
          <ControlledSelect
            disabled={isSubmitting}
            control={control}
            rules={{ required: 'Requis' }}
            name={nameof<TFormData>('boostPackageName')}
            placeholder='Choisir un package'
            options={getSelectOptions('BoostPackageNameEnum')} />
        </div>
      }

      <div className='flex flex-col gap-2'>
        <LabelRequired>Motif de l'ajout</LabelRequired>
        <ControlledSelect
          disabled={isSubmitting}
          control={control}
          rules={{ required: 'Requis' }}
          name={nameof<TFormData>('product')}
          placeholder={`Indiquer le motif de l'ajout`}
          options={getSelectOptions('ProductsEnum', {
            filter: option => PRODUCT_OPTIONS_BY_TAB[mainTab as MainTabEnum]?.includes(option.value as ProductsEnum)
          })} />
      </div>

      <div className='flex flex-col gap-2'>
        <LabelRequired>Description</LabelRequired>
        <ControlledTextarea
          disabled={isSubmitting}
          control={control}
          rules={{ required: 'Requis' }}
          name={nameof<TFormData>('comment')}
          placeholder={'Votre commentaire'} />
      </div>
    </div>
  );

};

export default FormSetCoinsToUserBody;
